import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { avatarUrl } from '@/lib/avatar'
import { userCardHandler, userContextHandler } from '@/store/contextMenu'
import { coarsePointer } from '@/lib/mobile'
import { bindMessageSwipe, messageGestureIgnore } from '@/lib/messageSwipe'
import { toReadable } from '@/lib/autocomplete'
import { MessageTextSelection } from './MessageTextSelection'
import { ChevronRight, Eye, Flag, Forward, MessageSquare, MessageSquarePlus, MoreHorizontal, Pencil, Pin, PinOff, Reply, SmilePlus, Trash2 } from 'lucide-react'
import { DeleteMessageModal } from './DeleteMessageModal'
import { MessageMenu } from './MessageMenu'
import { EmojiPicker } from './EmojiPicker'
import { reactionKey } from '@/lib/emoji'
import { emojiImageUrl, isEmojiOnly, stickerImageUrl } from '@/lib/markdown'
import { socialApi } from '@/lib/socialApi'
import { plural } from '@/lib/guildUi'
import { renderMarkdown } from '@/lib/markdown'
import { mentionNames } from '@/lib/mentions'
import { commandInvocation, isEphemeral, isLoading, responseExpired } from '@/lib/interactions'
import { cx } from '@/lib/cx'
import { MessageFlags, MessageType, type Emoji, type Message, type Reaction, type ReactionEmoji, type User } from '@/types/api'
import { EmbedCard } from './EmbedCard'
import { InviteCard } from './InviteCard'
import { inviteCodes, isInviteUrl } from '@/lib/inviteLinks'
import { AttachmentView } from './AttachmentView'
import { PollCard } from './PollCard'
import { VoicePlayer } from './VoicePlayer'
import { ForwardedBlock } from './ForwardedBlock'
import { ForwardModal } from './ForwardModal'
import { MessageComponents, type ComponentEnv } from './MessageComponents'
import { displayName, useSocialStore } from '@/store/social'
import { formatDateTime, formatTime } from '@/lib/format'
import { Avatar } from '../Avatar'
import base from '../Message.module.css'
import { MessageEditor } from './MessageEditor'
import styles from './MessageRow.module.css'
import { StyledName } from '../StyledName'
import { ServerTag } from '../ServerTag'

interface Props {
  message: Message
  continuation: boolean
  mine: boolean
  canEditPublication?: boolean
  /** На сервере: MANAGE_MESSAGES позволяет удалять чужие. */
  canDelete?: boolean
  onSelectForDeletion?: ((m: Message) => void) | undefined
  /** Цвет имени автора по роли и имя роли для упоминаний. */
  nameColor?: string | undefined
  roleName?: ((id: string) => { name: string; color?: string | undefined } | undefined) | undefined
  channelRef?: ((id: string) => { name: string; onClick?: (() => void) | undefined } | undefined) | undefined
  users: Record<string, User>
  /** Сервер канала — для пунктов модерации в меню автора. */
  guildId?: string | undefined
  onReply: (m: Message) => void
  swipeReplyEnabled?: boolean
  onEdit: (m: Message, content: string) => Promise<void>
  onDelete: (m: Message) => Promise<void>
  onRetry: (m: Message) => void
  /** Жалоба на чужое сообщение. */
  onReport?: ((m: Message) => void) | undefined
  /** Скрыть превью ссылок: автор или модератор с MANAGE_MESSAGES. */
  onSuppress?: ((m: Message) => Promise<void>) | undefined
  /** Начать ветку от сообщения (только в текстовых каналах с CREATE_PUBLIC_THREADS). */
  onThread?: ((m: Message) => void) | undefined
  onOpenThread?: ((threadId: string) => void) | undefined
  /** Реакции: поставить или снять свою. */
  onReact?: ((m: Message, emoji: ReactionEmoji) => Promise<void>) | undefined
  /** Закрепить или открепить (MANAGE_MESSAGES на сервере, любой получатель в ЛС). */
  onPin?: ((m: Message, pinned: boolean) => Promise<void>) | undefined
  /** Кастомные эмодзи для пикера: текущий сервер первым. */
  customEmoji?: { guild: string; emojis: Emoji[] }[] | undefined
  channelId: string
  /** Клик по цитате ответа: прокрутить к исходному сообщению. */
  onJump?: ((messageId: string) => void) | undefined
  /** Кнопки и списки бота: как выполнить нажатие. Без него они видны, но не нажимаются. */
  interact?: ComponentEnv | undefined
}

const colorOf = (id: string) => `hsl(${Number(id.slice(-4)) % 360} 55% 50%)`

export function MessageRow({ guildId, message, continuation, mine, canEditPublication = false, canDelete, onSelectForDeletion, nameColor, roleName, channelRef, users, onReply, swipeReplyEnabled = true, onEdit, onDelete, onRetry, onReport, onSuppress, onThread, onOpenThread, onReact, onPin, customEmoji, channelId, onJump, interact }: Props) {
  // опрос, голосовое и пересланное не правят, как у Discord
  const forwarded = message.message_reference?.type === 1 && Boolean(message.message_snapshots?.length)
  const editable = (mine || canEditPublication) && !message.poll && !forwarded && !((message.flags ?? 0) & MessageFlags.IsVoiceMessage)
  // переслать можно обычное сообщение и ответ, кроме опроса, ещё не отправленного и ответа «только вам»
  const forwardable = (message.type === MessageType.Default || message.type === MessageType.Reply) && !message.poll && !message.pending && !message.failed && !message.id.startsWith('pending:') && !isEphemeral(message)
  const [forwarding, setForwarding] = useState(false)
  const [editing, setEditing] = useState(false)
  const [picker, setPicker] = useState(false)
  const [selectingText, setSelectingText] = useState(false)
  // меню сообщения: у курсора по правой кнопке или под «…» в панели действий
  const [menu, setMenu] = useState<{ x: number; y: number; align?: 'right' } | null>(null)
  /*
   * Наведения у пальца нет, и панель действий по нему не появится. По тапу на сообщение
   * показываем её; повторный тап прячет. Мышью всё как раньше — по наведению.
   */
  const [touched, setTouched] = useState(false)
  const rowRef = useRef<HTMLElement | null>(null)
  const closeMenu = useCallback(() => { setMenu(null); setTouched(false) }, [])
  const startEdit = () => {
    closeMenu()
    setPicker(false)
    setEditing(true)
  }
  // Панель относится только к последнему тапу: новый тап или прокрутка убирают её.
  useEffect(() => {
    if (!touched) return
    const onDown = (e: PointerEvent) => {
      if (!rowRef.current?.contains(e.target as Node)) setTouched(false)
    }
    const dismiss = () => setTouched(false)
    const selected = () => { if (window.getSelection()?.toString()) dismiss() }
    document.addEventListener('pointerdown', onDown, true)
    document.addEventListener('scroll', dismiss, true)
    document.addEventListener('selectionchange', selected)
    window.addEventListener('resize', dismiss)
    return () => {
      document.removeEventListener('pointerdown', onDown, true)
      document.removeEventListener('scroll', dismiss, true)
      document.removeEventListener('selectionchange', selected)
      window.removeEventListener('resize', dismiss)
    }
  }, [touched])
  const [confirmDelete, setConfirmDelete] = useState(false)
  const jumbo = isEmojiOnly(message.content)
  // клик по упоминанию открывает карточку того, кого упомянули
  const openCard = (id: string, e: { preventDefault: () => void; clientX: number; clientY: number }) =>
    userCardHandler(id, { guildId })(e)
  const author = users[message.author.id] ?? message.author
  const name = displayName(author)
  const time = new Date(message.timestamp)
  const userName = mentionNames(message, users)
  // приглашения на сервер — своей карточкой с кнопкой; их обычное превью прячем, чтобы не было двух
  const invites = useMemo(() => inviteCodes(message.content), [message.content])
  const embeds = (((message.flags ?? 0) & MessageFlags.SuppressEmbeds) === 0 ? (message.embeds ?? []) : []).filter((e) => !(invites.length && e.url && isInviteUrl(e.url)))
  const removeEmbed = (mine || canDelete) && onSuppress ? () => void onSuppress(message) : undefined
  const thread = message.thread ?? null
  const threadCount = thread?.message_count ?? 0
  const ref = message.message_reference
  // цитата ведёт к исходному сообщению, если оно в этом же канале
  const jumpToReply = onJump && ref && (!ref.channel_id || ref.channel_id === channelId) ? () => onJump(ref.message_id) : undefined
  // ответ приложения: кто вызвал команду, «только вам», «думает…»
  const invocation = commandInvocation(message)
  const ephemeral = isEphemeral(message)
  const loading = isLoading(message)
  const dismiss = useSocialStore((s) => s.dismissEphemeral)
  // «только вам» нет на сервере: ни ответить, ни отреагировать, ни закрепить — только скрыть
  const actionable = !message.pending && !message.failed && !editing && !ephemeral
  // Updating the message/callback during a gesture must not detach its owner.
  const reply = useRef<() => void>(() => onReply(message))
  useEffect(() => { reply.current = () => { setTouched(false); onReply(message) } }, [onReply, message])
  useEffect(() => {
    const row = rowRef.current
    if (!coarsePointer || !actionable || !swipeReplyEnabled || !row) return
    return bindMessageSwipe(row, () => reply.current())
  }, [actionable, swipeReplyEnabled, message.id])

  const submitEdit = async (text: string) => {
    const content = text.trim()
    if (!content || content === message.content) {
      setEditing(false)
      return
    }
    await onEdit(message, content)
    setEditing(false)
  }
  // имена для правки: те же, что у упоминаний в самом сообщении
  const editNames = (kind: 'user' | 'role' | 'channel', id: string) =>
    kind === 'user' ? userName(id) : kind === 'role' ? roleName?.(id)?.name : channelRef?.(id)?.name

  return (
    <article
      ref={rowRef}
      className={cx(base.message, continuation && base.continuation, (message.referenced_message || (message.message_reference && !forwarded) || invocation) && base.withReply, styles.row, message.pending && styles.pending, message.failed && styles.failed)}
      data-touched={touched ? '' : undefined}
      data-menu={menu ? '' : undefined}
      data-swipe-reply={actionable && swipeReplyEnabled ? '' : undefined}
      onClick={coarsePointer ? (e) => {
        if (window.getSelection()?.toString()) { setTouched(false); return }
        if (actionable && !e.defaultPrevented && !(e.target as HTMLElement).closest(messageGestureIgnore)) setTouched((v) => !v)
      } : undefined}
      onContextMenu={(e) => {
        // имя и аватар открывают меню пользователя; ссылки, картинки и выделенный текст — меню браузера
        if (coarsePointer || e.defaultPrevented || !actionable) return
        if ((e.target as HTMLElement).closest(messageGestureIgnore) || window.getSelection()?.toString()) return
        e.preventDefault()
        setMenu({ x: e.clientX, y: e.clientY })
      }}
      aria-label={`Сообщение от ${name}`}
    >
      {actionable && swipeReplyEnabled ? <span className={styles.replySwipe} aria-hidden="true"><Reply size={18} strokeWidth={2} /></span> : null}
      {!continuation ? (
        <span data-message-gesture-ignore className={styles.authorTrigger} onClick={userCardHandler(author.id, { user: author, guildId })} onContextMenu={userContextHandler(author.id, { user: author, guildId })}>
          <Avatar initial={name.slice(0, 1).toUpperCase()} color={colorOf(author.id)} size={40} className={base.avatar} src={avatarUrl(author.id, author.avatar)} decoration={author.avatar_decoration_data} />
        </span>
      ) : (
        <time className={base.hoverTime} dateTime={message.timestamp}>{formatTime(time)}</time>
      )}
      {message.referenced_message ? (
        <div
          className={cx(base.reply, jumpToReply && styles.replyJump)}
          role={jumpToReply ? 'button' : undefined}
          tabIndex={jumpToReply ? 0 : undefined}
          data-tooltip={jumpToReply ? 'Перейти к сообщению' : undefined}
          onClick={jumpToReply}
          onKeyDown={jumpToReply ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); jumpToReply() } } : undefined}
        >
          <Avatar initial={displayName(message.referenced_message.author).slice(0, 1).toUpperCase()} color={colorOf(message.referenced_message.author.id)} size={16} src={avatarUrl(message.referenced_message.author.id, message.referenced_message.author.avatar)} />
          <span className={base.replyAuthor}>{displayName(message.referenced_message.author)}</span>
          <span className={base.replyText}>{message.referenced_message.content ? renderMarkdown(message.referenced_message.content, { userName, inline: true }) : '…'}</span>
        </div>
      ) : message.message_reference && !forwarded ? (
        <div className={base.reply}><span className={base.replyText}>Исходное сообщение удалено</span></div>
      ) : null}
      {invocation ? <Invocation user={users[invocation.user.id] ?? invocation.user} command={invocation.name} guildId={guildId} /> : null}
      {!continuation ? (
        <header className={base.header}>
          <span data-message-gesture-ignore className={cx(base.author, styles.authorTrigger)} style={nameColor ? { color: nameColor } : undefined} onClick={userCardHandler(author.id, { user: author, guildId })} onContextMenu={userContextHandler(author.id, { user: author, guildId })}><StyledName name={name} style={author.display_name_styles} /></span>
          {author.primary_guild ? <ServerTag tag={author.primary_guild.tag} color={author.primary_guild.color} /> : null}
          {author.bot ? <span className={base.botTag}>БОТ</span> : null}
          <time className={base.time} dateTime={message.timestamp}>{formatDateTime(time)}</time>
          {message.pinned ? <span className={styles.pinnedTag} data-tooltip="Закреплено"><Pin size={12} /> закреплено</span> : null}
        </header>
      ) : null}
      {editing ? (
        <MessageEditor content={message.content} name={editNames} onSave={submitEdit} onCancel={() => setEditing(false)} />
      ) : (
        <div className={base.body}>
          {loading ? (
            <Thinking name={name} expired={responseExpired(message)} />
          ) : (
            renderMarkdown(message.content, { userName, ...(roleName ? { role: roleName } : {}), ...(channelRef ? { channel: channelRef } : {}), onUser: openCard, jumbo })
          )}
          {message.edited_timestamp && !loading ? <span className={base.edited}>(изменено)</span> : null}
          {message.failed ? (
            <span className={styles.failedText}>
              Не отправлено. <button type="button" className={styles.retry} onClick={() => onRetry(message)}>Повторить</button>
            </span>
          ) : null}
          {forwarded ? <ForwardedBlock message={message} onRemoveEmbed={removeEmbed} render={{ userName, ...(roleName ? { role: roleName } : {}), ...(channelRef ? { channel: channelRef } : {}) }} /> : null}
          {message.poll ? <PollCard message={message} channelId={channelId} mine={mine} /> : null}
          {message.attachments?.length ? (
            (message.flags ?? 0) & MessageFlags.IsVoiceMessage && message.attachments[0] ? <VoicePlayer attachment={message.attachments[0]} /> : <AttachmentView attachments={message.attachments} />
          ) : null}
          {message.sticker_items?.length ? (
            <div className={styles.stickers}>
              {message.sticker_items.map((s) => <img key={s.id} src={stickerImageUrl(s)} alt={s.name} data-tooltip={s.name} className={styles.sticker} loading="lazy" draggable={false} />)}
            </div>
          ) : null}
          {invites.length ? (
            <div className={styles.embeds}>
              {invites.map((code) => <InviteCard key={code} code={code} />)}
            </div>
          ) : null}
          {embeds.length ? (
            <div className={styles.embeds}>
              {embeds.map((e, i) => <EmbedCard key={i} embed={e} onRemove={removeEmbed} render={{ userName, ...(roleName ? { role: roleName } : {}), ...(channelRef ? { channel: channelRef } : {}) }} />)}
            </div>
          ) : null}
          {message.components?.length && !loading ? <MessageComponents message={message} env={interact} /> : null}
          {message.reactions?.length ? (
            <ReactionBar reactions={message.reactions} channelId={channelId} messageId={message.id} onToggle={onReact ? (e) => void onReact(message, e) : undefined} onAdd={onReact ? () => setPicker(true) : undefined} />
          ) : null}
          {thread ? (
            <button type="button" className={styles.threadChip} onClick={() => onOpenThread?.(thread.id)} aria-label={`Открыть ветку ${thread.name ?? ''}`}>
              <MessageSquare size={16} />
              <strong className={styles.threadName}>{thread.name}</strong>
              <span className={styles.threadMeta}>
                {threadCount} {plural(threadCount, 'сообщение', 'сообщения', 'сообщений')}
                {thread.thread_metadata?.archived ? ' · в архиве' : ''}
              </span>
              <ChevronRight size={14} />
            </button>
          ) : null}
          {ephemeral ? (
            <div className={styles.ephemeral}>
              <Eye size={14} aria-hidden="true" />
              <span>Только вы видите это сообщение</span>
              <span aria-hidden="true">·</span>
              <button type="button" className={styles.dismiss} onClick={() => dismiss(message.channel_id, message.id)}>Скрыть</button>
            </div>
          ) : null}
        </div>
      )}
      {actionable ? (
        <div className={styles.toolbar} role="toolbar" aria-label="Действия с сообщением" onClickCapture={() => setTouched(false)}>
          {onReact ? <button type="button" data-tooltip="Добавить реакцию" aria-label="Добавить реакцию" onClick={() => setPicker((v) => !v)}><SmilePlus size={18} /></button> : null}
          <button type="button" data-tooltip="Ответить" aria-label="Ответить" onClick={() => onReply(message)}><Reply size={18} /></button>
          {forwardable ? <button type="button" className={styles.secondaryAction} data-tooltip="Переслать" aria-label="Переслать" onClick={() => setForwarding(true)}><Forward size={18} /></button> : null}
          {onPin ? (
            <button type="button" className={styles.secondaryAction} data-tooltip={message.pinned ? 'Открепить' : 'Закрепить'} aria-label={message.pinned ? 'Открепить' : 'Закрепить'} onClick={() => void onPin(message, !message.pinned)}>
              {message.pinned ? <PinOff size={18} /> : <Pin size={18} />}
            </button>
          ) : null}
          {editable ? <button type="button" className={styles.secondaryAction} data-tooltip="Изменить" aria-label="Изменить" onClick={startEdit}><Pencil size={18} /></button> : null}
          {mine || canDelete ? (
            <button
              type="button"
              data-tooltip="Удалить (с Shift — сразу, без подтверждения)"
              aria-label="Удалить"
              className={cx(styles.danger, styles.secondaryAction)}
              onClick={(e) => (e.shiftKey ? void onDelete(message) : setConfirmDelete(true))}
            >
              <Trash2 size={18} />
            </button>
          ) : null}
          {onThread && !thread ? <button type="button" className={styles.secondaryAction} data-tooltip="Создать ветку" aria-label="Создать ветку" onClick={() => onThread(message)}><MessageSquarePlus size={18} /></button> : null}
          {!mine && onReport ? <button type="button" className={styles.secondaryAction} data-tooltip="Пожаловаться" aria-label="Пожаловаться" onClick={() => onReport(message)}><Flag size={18} /></button> : null}
          <button
            type="button"
            data-tooltip="Ещё"
            aria-label="Ещё"
            aria-haspopup="menu"
            aria-expanded={Boolean(menu)}
            onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect()
              setMenu({ x: r.right, y: r.bottom + 4, align: 'right' })
            }}
          >
            <MoreHorizontal size={18} />
          </button>
        </div>
      ) : null}
      {menu ? (
        <MessageMenu
          message={message}
          guildId={guildId}
          channelId={channelId}
          x={menu.x}
          y={menu.y}
          align={menu.align}
          onClose={closeMenu}
          onReact={onReact ? () => setPicker(true) : undefined}
          onReply={() => onReply(message)}
          onForward={forwardable ? () => setForwarding(true) : undefined}
          onThread={onThread && !thread ? () => onThread(message) : undefined}
          onEdit={editable ? startEdit : undefined}
          onPin={onPin ? () => void onPin(message, !message.pinned) : undefined}
          onReport={!mine && onReport ? () => onReport(message) : undefined}
          onDelete={mine || canDelete ? () => setConfirmDelete(true) : undefined}
          onSelectForDeletion={onSelectForDeletion ? () => onSelectForDeletion(message) : undefined}
          onSelectText={coarsePointer && message.content ? () => setSelectingText(true) : undefined}
        />
      ) : null}
      {selectingText ? <MessageTextSelection text={toReadable(message.content, editNames).text} onClose={() => setSelectingText(false)} /> : null}
      {forwarding ? <ForwardModal message={message} onClose={() => setForwarding(false)} /> : null}
      {picker && onReact ? (
        <div className={styles.pickerAnchor}>
          <EmojiPicker custom={customEmoji ?? []} onClose={() => setPicker(false)} onPick={(e) => { setPicker(false); void onReact(message, e) }} />
        </div>
      ) : null}
      {confirmDelete ? (
        <DeleteMessageModal
          message={message}
          author={author}
          name={name}
          render={{ userName, ...(roleName ? { role: roleName } : {}), ...(channelRef ? { channel: channelRef } : {}) }}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={async () => {
            await onDelete(message)
            setConfirmDelete(false)
          }}
        />
      ) : null}
    </article>
  )
}

/**
 * Строка над ответом на слэш-команду: «Вася использовал(а) /музыка играй». Крючок от аватарки
 * тот же, что у ответа на сообщение, — так делает и Discord.
 */
function Invocation({ user, command, guildId }: { user: User; command: string; guildId?: string | undefined }) {
  const name = displayName(user)
  return (
    <div className={cx(base.reply, styles.invocation)}>
      <Avatar initial={name.slice(0, 1).toUpperCase()} color={colorOf(user.id)} size={16} src={avatarUrl(user.id, user.avatar)} />
      <span data-message-gesture-ignore className={cx(base.replyAuthor, styles.authorTrigger)} onClick={userCardHandler(user.id, { user, guildId })} onContextMenu={userContextHandler(user.id, { user, guildId })}>
        {name}
      </span>
      <span>использовал(а)</span>
      <span className={styles.command}>{command ? `/${command}` : 'команду'}</span>
    </div>
  )
}

/** Отложенный ответ (флаг 128): «Музыка думает…». Через 15 минут токен взаимодействия истёк — ответа не будет. */
function Thinking({ name, expired }: { name: string; expired: boolean }) {
  if (expired) return <span className={styles.noResponse}>Приложение не ответило</span>
  return (
    <span className={styles.thinking} role="status">
      {name} думает
      <span className={styles.thinkingDots} aria-hidden="true">
        <i />
        <i />
        <i />
      </span>
    </span>
  )
}

/** Ряд реакций под сообщением: клик — поставить или снять свою, наведение — кто поставил. */
function ReactionBar({ reactions, channelId, messageId, onToggle, onAdd }: { reactions: Reaction[]; channelId: string; messageId: string; onToggle?: ((e: ReactionEmoji) => void) | undefined; onAdd?: (() => void) | undefined }) {
  const [who, setWho] = useState<Record<string, string>>({})
  const load = (r: Reaction) => {
    const key = reactionKey(r.emoji)
    if (who[key]) return
    socialApi
      .reactionUsers(channelId, messageId, key)
      .then((users) => setWho((w) => ({ ...w, [key]: users.map((u) => displayName(u)).join(', ') || '—' })))
      .catch(() => setWho((w) => ({ ...w, [key]: '' })))
  }
  return (
    <div className={styles.reactions} role="group" aria-label="Реакции">
      {reactions.map((r) => {
        const key = reactionKey(r.emoji)
        const title = who[key] ? `${who[key]} · ${r.emoji.id ? `:${r.emoji.name}:` : r.emoji.name}` : r.emoji.id ? `:${r.emoji.name}:` : r.emoji.name
        return (
          <button key={key} type="button" className={cx(styles.reaction, r.me && styles.reactionMine)} data-tooltip={title} aria-pressed={r.me} aria-label={`Реакция ${r.emoji.name}, ${r.count}`} onMouseEnter={() => load(r)} onClick={() => onToggle?.(r.emoji)}>
            {r.emoji.id ? <img src={emojiImageUrl(r.emoji.id, r.emoji.animated)} alt={`:${r.emoji.name}:`} className={styles.reactionImg} draggable={false} /> : <span className={styles.reactionChar}>{r.emoji.name}</span>}
            <span className={styles.reactionCount}>{r.count}</span>
          </button>
        )
      })}
      {onAdd ? <button type="button" className={styles.reactionAdd} data-tooltip="Добавить реакцию" aria-label="Добавить реакцию" onClick={onAdd}><SmilePlus size={16} /></button> : null}
    </div>
  )
}
